import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import { fileNameEdit } from 'helpers/FileUtils';
import moment from 'moment';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/store_vs_village/');

const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData2: [],
  lineChartData: [],
  total: {},
};

const useInvoicesTickets = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const currentYear = moment(
    searchParams.get('by_date_from_ytd'),
    'DD-MM-YYYY',
  ).year();

  const [columns, setColumns] = useState([
    //   Incidenza Sales Store YTD: 100
    // Incidenza categoria YTD vs LY: 0
    // Store YTD vs LY: null

    {
      // Periodo: "Totale"
      title: t('Periodo'),
      dataIndex: 'Periodo',
    },
    {
      // Sales Store YTD: 0
      title: t('Sales Store YTD'),
      dataIndex: 'Sales Store YTD',
      render: (data) => Helpers.numberWithCommas(data, ' €'),
    },

    {
      // Sales Store LY: 244270
      title: t('Sales Store LY'),
      dataIndex: 'Sales Store LY',
      render: (data) => Helpers.numberWithCommas(data, ' €'),
    },
    {
      // Sales Store LY: 244270
      title: t('Store YTD vs LY'),
      dataIndex: 'Store YTD vs LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      // Sales Store LY: 244270
      title: t('Store sales %'),
      dataIndex: 'Incidenza Sales Store YTD',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('DUAL GENDER Sales %'),
      keyValue: 'commodity_category',
      dataIndex: 'DUAL GENDER Sales %',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      // Incidenza Village YTD vs LY: -714011.95
      title: t('Incidenza categoria YTD vs LY'),
      dataIndex: 'Incidenza categoria YTD vs LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('LUXURY Sales %'),
      dataIndex: 'LUXURY Sales %',
      keyValue: 'prestige_level',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza Segmento YTD vs LY'),
      dataIndex: 'Incidenza Segmento YTD vs LY',
      // Incidenza Segmento YTD vs LY: 0
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Village Sales %'),
      dataIndex: 'Village sales %',
      // Village sales %: 100
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza Village YTD vs LY'),
      dataIndex: 'Incidenza Village YTD vs LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
  ]);
  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;
    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter(
      (column) =>
        tableKeys.includes(column.dataIndex) ||
        tableKeys.includes(column.keyValue),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row fixed>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(
            ({ keyValue, dataIndex, render = (v) => v, ...rest }) => {
              let val = total[dataIndex];
              if (keyValue) {
                val = total[keyValue].value;
              }
              return (
                <Table.Summary.Cell kry={dataIndex}>
                  <Text strong>{render(val)}</Text>
                </Table.Summary.Cell>
              );
            },
          )}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data, currentYear, columns]);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let data, total;

        if (!isAllReports) {
          const {
            data: { data: backData, total: backTotal },
          } = await api.getFootfalls({ params });
          data = backData;
          total = backTotal;
        } else {
          const { data: backData, total: backTotal } = allReportsData;
          data = backData;
          total = backTotal;
        }

        if (!data) return;

        const lineChartData1 = {
          id: 'Store Sales %',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Incidenza Sales Store YTD'],
          })),
        };
        const lineChartData2 = {
          id: total?.commodity_category?.title,
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item[total?.commodity_category?.title],
          })),
        };

        const lineChartData3 = {
          id: total?.prestige_level?.title,
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item[total?.prestige_level?.title],
          })),
        };

        const lineChartData4 = {
          id: 'Village Sales %',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Village sales %'],
          })),
        };

        setData({
          tableData: data,
          lineChartData: [
            lineChartData1,
            lineChartData2,
            lineChartData3,
            lineChartData4,
          ],
          total,
        });
        setErrors([]);
        setColumns((s) => {
          return s.map((i) => {
            if (i.keyValue === 'commodity_category') {
              return {
                ...i,
                title: total.commodity_category.title,
                dataIndex: total.commodity_category.title,
              };
            }
            if (i.keyValue === 'prestige_level') {
              return {
                ...i,
                title: total.prestige_level.title,
                dataIndex: total.prestige_level.title,
              };
            }
            return { ...i };
          });
        });
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    columns,
    data,
    filters,
    format,
    summary,
    loading,
    handleDownloadExcel,
    title,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesTickets;
